/* eslint-disable no-console */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ContentLoader from 'react-content-loader';
import { formatRoute } from 'react-router-named-routes';
// graphQL
import gql from 'graphql-tag';
// adopt
import { adopt } from 'react-adopt';
// global variables
import { userKinds } from 'config';
// our components
import { USER } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import { Query, Mutation } from 'components/common/Query';
import { DELETE_USERS_MUTATION } from 'components/User/List';
import UserUpdate from './Update';

/**
 * Fragment in AdminUserType
 * */
const userUpdateFragment = gql`
  fragment AdminUserData on AdminUserInterface {
    id
    email
    fullname
    phone
    rank
    userType
    cardProvider
    photo {
      gcsKey
      url
    }
    allowedIps
    negativeCredit
  }
`;

/**
 * Query to fetch user
 */
export const ADMIN_USER_QUERY = gql`
  query AdminUser($id: ID!) {
    adminUser(id: $id) {
      ...AdminUserData
      ... on AgencyType {
        commissionLead
        commissionLeadRevolico
        paymentMethod
      }
      ... on UserAgencyType {
        leadAgency {
          fullname
        }
        lead
      }
    }
  }
  ${userUpdateFragment}
`;

/**
 * Mutation to update user
 */
export const UPDATE_USER_MUTATION = gql`
  mutation(
    $id: ID
    $fullname: String
    $email: String
    $phone: String
    $password1: String
    $password2: String
    $rank: UserRank
    $cardProvider: AdminEditUserFormCardProvider
    $photo: String
    $allowedIps: [String]
    $negativeCredit: Boolean
  ) {
    updateAdminUser(
      input: {
        id: $id
        fullname: $fullname
        email: $email
        phone: $phone
        password1: $password1
        password2: $password2
        rank: $rank
        cardProvider: $cardProvider
        photo: $photo
        allowedIps: $allowedIps
        negativeCredit: $negativeCredit
      }
    ) {
      user {
        ...AdminUserData
      }
      errors {
        field
        messages
      }
      clientMutationId
    }
  }
  ${userUpdateFragment}
`;

/**
 * Mutation to update user
 */
export const UPDATE_AGENCY_MUTATION = gql`
  mutation(
    $id: ID
    $fullname: String
    $email: String
    $phone: String
    $password1: String
    $password2: String
    $rank: UserRank
    $cardProvider: AdminEditAgencyFormCardProvider
    $photo: String
    $commissionLead: Float!
    $commissionLeadRevolico: Float!
    $allowedIps: [String]
    $negativeCredit: Boolean
    $paymentMethod: AdminEditAgencyFormPaymentMethod!
  ) {
    updateAgency(
      input: {
        id: $id
        fullname: $fullname
        email: $email
        phone: $phone
        password1: $password1
        password2: $password2
        rank: $rank
        cardProvider: $cardProvider
        photo: $photo
        commissionLead: $commissionLead
        commissionLeadRevolico: $commissionLeadRevolico
        allowedIps: $allowedIps
        negativeCredit: $negativeCredit
        paymentMethod: $paymentMethod
      }
    ) {
      agency {
        ...AdminUserData
        commissionLead
        commissionLeadRevolico
      }
      errors {
        field
        messages
      }
      clientMutationId
    }
  }
  ${userUpdateFragment}
`;

/**
 * Mutation to update user
 */
export const UPDATE_USER_AGENCY_MUTATION = gql`
  mutation(
    $id: ID
    $fullname: String
    $email: String
    $phone: String
    $rank: UserRank
    $cardProvider: AdminEditUserAgencyFormCardProvider
    $photo: String
    $lead: AdminEditUserAgencyFormLead
    $allowedIps: [String]
    $negativeCredit: Boolean
  ) {
    updateUserAgency(
      input: {
        id: $id
        fullname: $fullname
        email: $email
        phone: $phone
        rank: $rank
        cardProvider: $cardProvider
        photo: $photo
        lead: $lead
        allowedIps: $allowedIps
        negativeCredit: $negativeCredit
      }
    ) {
      userAgency {
        ...AdminUserData
        lead
      }
      errors {
        field
        messages
      }
      clientMutationId
    }
  }
  ${userUpdateFragment}
`;

/**
 * @class
 * @extends Component
 */
class UserUpdateContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      errors: [],
    };
    this.Compose = adopt({
      mutateUserDelete: this.userDelete,
      mutateUserUpdate: this.userUpdate,
      mutateAgencyUpdate: this.agencyUpdate,
      mutateUserAgencyUpdate: this.userAgencyUpdate,
      query: this.user,
    });
  }

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   */
  user = ({ render }) => {
    const { id } = this.props;
    return (
      <Query query={ADMIN_USER_QUERY} variables={{ id }} fetchPolicy="no-cache">
        {({ data, loading }) => render({ data, loading })}
      </Query>
    );
  };

  /**
   * @description
   * Mutation delete callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  userDelete = ({ render }) => (
    <Mutation
      mutation={DELETE_USERS_MUTATION}
      onCompleted={this.mutationUserDeleteCompleted}
    >
      {(handleUserDelete, { loading }) =>
        render({ handleUserDelete, loadingDelete: loading })
      }
    </Mutation>
  );

  /**
   * @description
   * Mutation update callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  userUpdate = ({ render }) => (
    <Mutation
      mutation={UPDATE_USER_MUTATION}
      onCompleted={result =>
        this.mutationUpdateCompleted(result, 'updateAdminUser')
      }
    >
      {(handleUserUpdate, { loading }) =>
        render({ handleUserUpdate, loadingUpdate: loading })
      }
    </Mutation>
  );

  /**
   * @description
   * Mutation update callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  agencyUpdate = ({ render }) => (
    <Mutation
      mutation={UPDATE_AGENCY_MUTATION}
      onCompleted={result =>
        this.mutationUpdateCompleted(result, 'updateAgency')
      }
    >
      {(handleAgencyUpdate, { loading }) =>
        render({ handleAgencyUpdate, loadingAgencyUpdate: loading })
      }
    </Mutation>
  );

  /**
   * @description
   * Mutation update callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  userAgencyUpdate = ({ render }) => (
    <Mutation
      mutation={UPDATE_USER_AGENCY_MUTATION}
      onCompleted={result =>
        this.mutationUpdateCompleted(result, 'updateUserAgency')
      }
    >
      {(handleUserAgencyUpdate, { loading }) =>
        render({ handleUserAgencyUpdate, loadingUserAgencyUpdate: loading })
      }
    </Mutation>
  );

  /**
   * @description
   * OnComplete delete mutation.
   *
   * @param {Object} deleteAdminUser - Mutation result.
   */
  mutationUserDeleteCompleted = async ({ deleteAdminUser }) => {
    const history = this.context;
    const { errors } = deleteAdminUser;
    if (errors) {
      this.setState({ errors });
    } else history.push(USER.LIST);
  };

  /**
   * @description
   * OnComplete update mutation.
   *
   * @param {Object} result - Mutation result.
   * @param {string} operation - Operation name.
   */
  mutationUpdateCompleted = async (result, operation) => {
    const { id } = this.props;
    const history = this.context;
    const { errors } = result[operation];
    if (errors) {
      this.setState({ errors });
    } else {
      history.push(formatRoute(`${USER.DETAILS}#logs`, { id }));
    }
  };

  /**
   * @description
   * Render UserUpdate component
   *
   * @returns {*} UserUpdate component.
   */
  render() {
    const { errors } = this.state;
    const { id } = this.props;
    return (
      <this.Compose>
        {({
          mutateUserDelete: { handleUserDelete, loadingDelete },
          mutateUserUpdate: { handleUserUpdate, loadingUpdate },
          mutateAgencyUpdate: { handleAgencyUpdate, loadingAgencyUpdate },
          mutateUserAgencyUpdate: {
            handleUserAgencyUpdate,
            loadingUserAgencyUpdate,
          },
          query: { data, loading },
        }) => {
          if (loading)
            return (
              <ContentLoader
                height={841}
                width={752}
                speed={2}
                primaryColor="#d9d9d9"
                secondaryColor="#ecebeb"
              >
                <path
                  // eslint-disable-next-line max-len
                  d="M111,169 L117,169 C148.480231,169 174,194.519769 174,226 C174,257.480231 148.480231,283 117,283 L111,283 C79.5197693,283 54,257.480231 54,226 C54,194.519769 79.5197693,169 111,169 Z M39,37 L279,37 C282.865993,37 286,40.1340068 286,44 L286,69 C286,72.8659932 282.865993,76 279,76 L39,76 C35.1340068,76 32,72.8659932 32,69 L32,44 C32,40.1340068 35.1340068,37 39,37 Z M546.5,37 L691.5,37 C702.269553,37 711,45.7304474 711,56.5 C711,67.2695526 702.269553,76 691.5,76 L546.5,76 C535.730447,76 527,67.2695526 527,56.5 C527,45.7304474 535.730447,37 546.5,37 Z M247,181 L363,181 C365.209139,181 367,182.790861 367,185 L367,192 C367,194.209139 365.209139,196 363,196 L247,196 C244.790861,196 243,194.209139 243,192 L243,185 C243,182.790861 244.790861,181 247,181 Z M248,207 L670.559964,207 C673.321388,207 675.559964,209.238576 675.559964,212 L675.559964,226 C675.559964,228.761424 673.321388,231 670.559964,231 L248,231 C245.238576,231 243,228.761424 243,226 L243,212 C243,209.238576 245.238576,207 248,207 Z M247,275 L363,275 C365.209139,275 367,276.790861 367,279 L367,286 C367,288.209139 365.209139,290 363,290 L247,290 C244.790861,290 243,288.209139 243,286 L243,279 C243,276.790861 244.790861,275 247,275 Z M248,301 L670.559964,301 C673.321388,301 675.559964,303.238576 675.559964,306 L675.559964,320 C675.559964,322.761424 673.321388,325 670.559964,325 L248,325 C245.238576,325 243,322.761424 243,320 L243,306 C243,303.238576 245.238576,301 248,301 Z M247,354 L363,354 C365.209139,354 367,355.790861 367,358 L367,365 C367,367.209139 365.209139,369 363,369 L247,369 C244.790861,369 243,367.209139 243,365 L243,358 C243,355.790861 244.790861,354 247,354 Z M248,380 L670.559964,380 C673.321388,380 675.559964,382.238576 675.559964,385 L675.559964,399 C675.559964,401.761424 673.321388,404 670.559964,404 L248,404 C245.238576,404 243,401.761424 243,399 L243,385 C243,382.238576 245.238576,380 248,380 Z M247,522 L363,522 C365.209139,522 367,523.790861 367,526 L367,533 C367,535.209139 365.209139,537 363,537 L247,537 C244.790861,537 243,535.209139 243,533 L243,526 C243,523.790861 244.790861,522 247,522 Z M248,548 L670.559964,548 C673.321388,548 675.559964,550.238576 675.559964,553 L675.559964,567 C675.559964,569.761424 673.321388,572 670.559964,572 L248,572 C245.238576,572 243,569.761424 243,567 L243,553 C243,550.238576 245.238576,548 248,548 Z M572.5,719 L683.5,719 C698.687831,719 711,731.312169 711,746.5 C711,761.687831 698.687831,774 683.5,774 L572.5,774 C557.312169,774 545,761.687831 545,746.5 C545,731.312169 557.312169,719 572.5,719 Z M48.5,730 L181.5,730 C190.612698,730 198,737.387302 198,746.5 C198,755.612698 190.612698,763 181.5,763 L48.5,763 C39.3873016,763 32,755.612698 32,746.5 C32,737.387302 39.3873016,730 48.5,730 Z M247,443 L363,443 C365.209139,443 367,444.790861 367,447 L367,454 C367,456.209139 365.209139,458 363,458 L247,458 C244.790861,458 243,456.209139 243,454 L243,447 C243,444.790861 244.790861,443 247,443 Z M498,443 L614,443 C616.209139,443 618,444.790861 618,447 L618,454 C618,456.209139 616.209139,458 614,458 L498,458 C495.790861,458 494,456.209139 494,454 L494,447 C494,444.790861 495.790861,443 498,443 Z M248,469 L420,469 C422.761424,469 425,471.238576 425,474 L425,488 C425,490.761424 422.761424,493 420,493 L248,493 C245.238576,493 243,490.761424 243,488 L243,474 C243,471.238576 245.238576,469 248,469 Z M499,469 L671,469 C673.761424,469 676,471.238576 676,474 L676,488 C676,490.761424 673.761424,493 671,493 L499,493 C496.238576,493 494,490.761424 494,488 L494,474 C494,471.238576 496.238576,469 499,469 Z"
                  fill="#D8D8D8"
                />
              </ContentLoader>
            );
          return (
            <UserUpdate
              errors={errors}
              userData={data}
              submitLoading={
                loadingUpdate ||
                loadingAgencyUpdate ||
                loadingDelete ||
                loadingUserAgencyUpdate
              }
              userUpdate={({ userType, ...variables }) => {
                switch (userType) {
                  case userKinds.AGENCY:
                    handleAgencyUpdate({
                      variables: { ...variables },
                    });
                    break;

                  case userKinds.USER_AGENCY:
                    handleUserAgencyUpdate({
                      variables: { ...variables },
                    });
                    break;

                  default:
                    handleUserUpdate({
                      variables: { ...variables },
                    });
                    break;
                }
              }}
              userDelete={() => handleUserDelete({ variables: { ids: [id] } })}
            />
          );
        }}
      </this.Compose>
    );
  }
}

/**
 * UserUpdateContainer PropTypes
 * @param id - prop to know User Id
 */

UserUpdateContainer.propTypes = {
  id: PropTypes.string.isRequired,
};

export default UserUpdateContainer;
